import {CSSProperties} from 'react';

import {NEAR_BLACK, RED_LIGHTER, WHITE} from 'ui-library/Colors';

export interface ResidueColor {
  font: string;
  background: string;
}

interface InputResidueColor {
  bg: string;
  fgType?: 'white' | 'dark';
}

export const DefaultResidueColor: ResidueColor = {
  font: NEAR_BLACK,
  background: '',
};

export enum ColoringSchemeType {
  Mismatch = 'Mismatch',
  ProteinRasmol = 'ProteinRasmol',
  ProteinPolarity = 'ProteinPolarity',
  ProteinHydrophobicity = 'ProteinHydrophobicity',
}

export class ColoringScheme {
  private readonly _scheme: ColoringSchemeType;
  private readonly _name: string;
  private readonly _residueColors: Record<string, ResidueColor>;

  constructor(
    scheme: ColoringSchemeType,
    name: string,
    inputResidueColors: Record<string, InputResidueColor>
  ) {
    this._scheme = scheme;
    this._name = name;
    this._residueColors = {};
    for (const [key, color] of Object.entries(inputResidueColors)) {
      this._residueColors[key] = {
        background: color.bg,
        font: color.fgType === 'dark' ? NEAR_BLACK : WHITE,
      };
    }
  }

  get scheme(): ColoringSchemeType {
    return this._scheme;
  }

  get name(): string {
    return this._name;
  }

  public getResidueColor(residue: string): ResidueColor {
    const residueColor = this._residueColors[residue.toUpperCase()];
    if (residueColor != null) {
      return residueColor;
    } else {
      return DefaultResidueColor;
    }
  }
}

const DefaultColoringScheme: ColoringScheme = new ColoringScheme(ColoringSchemeType.Mismatch, 'Mismatch', {});

const RasmolColoringScheme: ColoringScheme = new ColoringScheme(ColoringSchemeType.ProteinRasmol, 'Rasmol', {
  A: {bg: '#808080'},
  C: {bg: '#E6E600', fgType: 'dark'},
  D: {bg: '#E60A0A'},
  F: {bg: '#3232AA'},
  G: {bg: '#C0C0C0', fgType: 'dark'},
  H: {bg: '#8282D2', fgType: 'dark'},
  I: {bg: '#0F820F'},
  K: {bg: '#145AFF'},
  N: {bg: '#00DCDC', fgType: 'dark'},
  P: {bg: '#DC9682', fgType: 'dark'},
  S: {bg: '#FA9600', fgType: 'dark'},
  W: {bg: '#B45AB4', fgType: 'dark'},
  M: {bg: '#E6E600', fgType: 'dark'},
  E: {bg: '#E60A0A'},
  Y: {bg: '#3232AA'},
  L: {bg: '#0F820F'},
  V: {bg: '#0F820F'},
  R: {bg: '#145AFF'},
  Q: {bg: '#00DCDC', fgType: 'dark'},
  T: {bg: '#FA9600', fgType: 'dark'},
});

const PolarityColoringScheme: ColoringScheme = new ColoringScheme(
  ColoringSchemeType.ProteinPolarity,
  'Polarity',
  {
    A: {bg: '#E6E600', fgType: 'dark'},
    C: {bg: '#00E600', fgType: 'dark'},
    D: {bg: '#E60000'},
    F: {bg: '#E6E600', fgType: 'dark'},
    G: {bg: '#E6E600', fgType: 'dark'},
    H: {bg: '#0000E6'},
    I: {bg: '#E6E600', fgType: 'dark'},
    K: {bg: '#0000E6'},
    N: {bg: '#00E600', fgType: 'dark'},
    P: {bg: '#E6E600', fgType: 'dark'},
    S: {bg: '#00E600', fgType: 'dark'},
    W: {bg: '#E6E600', fgType: 'dark'},
    M: {bg: '#E6E600', fgType: 'dark'},
    E: {bg: '#E60000'},
    Y: {bg: '#00E600', fgType: 'dark'},
    L: {bg: '#E6E600', fgType: 'dark'},
    V: {bg: '#E6E600', fgType: 'dark'},
    R: {bg: '#0000E6'},
    Q: {bg: '#00E600', fgType: 'dark'},
    T: {bg: '#00E600', fgType: 'dark'},
  }
);

const HydrophobicityColoringScheme: ColoringScheme = new ColoringScheme(
  ColoringSchemeType.ProteinHydrophobicity,
  'Hydrophobicity',
  {
    A: {bg: '#9D0062'},
    C: {bg: '#AD0052'},
    D: {bg: '#0700F8'},
    F: {bg: '#F0000F'},
    G: {bg: '#80007F'},
    H: {bg: '#2A00D5'},
    I: {bg: '#F0000F'},
    K: {bg: '#4800B7'},
    N: {bg: '#3C00C3'},
    P: {bg: '#B5004A'},
    S: {bg: '#5C00A3'},
    W: {bg: '#E0001F'},
    M: {bg: '#BC0043'},
    E: {bg: '#E60A0A'},
    Y: {bg: '#E0001F'},
    L: {bg: '#F0000F'},
    V: {bg: '#D2002D'},
    R: {bg: '#0000FF'},
    Q: {bg: '#4000BF'},
    T: {bg: '#73008C'},
  }
);

export const AaSequenceBlastColoringSchemes: Record<ColoringSchemeType, ColoringScheme> = {
  [ColoringSchemeType.Mismatch]: DefaultColoringScheme,
  [ColoringSchemeType.ProteinRasmol]: RasmolColoringScheme,
  [ColoringSchemeType.ProteinPolarity]: PolarityColoringScheme,
  [ColoringSchemeType.ProteinHydrophobicity]: HydrophobicityColoringScheme,
};

export const getResidueColorStyle = (
  scheme: ColoringSchemeType,
  residue: string,
  mismatch?: boolean
): CSSProperties => {
  if (scheme === ColoringSchemeType.Mismatch && mismatch) {
    return {
      backgroundColor: RED_LIGHTER,
      font: NEAR_BLACK,
    };
  }
  const {background, font} = AaSequenceBlastColoringSchemes[scheme].getResidueColor(residue);
  return {
    backgroundColor: background,
    color: font,
  };
};
